import type { SignChannel } from "./signApiClient";

export type TemplateFieldKind = "signature" | "initials" | "date" | "text" | "checkbox";

export type EditorField = {
  id: string;
  kind: TemplateFieldKind;
  page: number;
  x: number;
  y: number;
  width: number;
  height: number;
  signerRole?: string;
  dataPath?: string;
  required?: boolean;
};

export type EditorFieldMap = {
  fields: EditorField[];
};

export type EditorSignerRole = {
  role: string;
  label?: string;
  order: number;
  defaultChannel?: SignChannel;
};

export const DEFAULT_SIGNER_ROLE_PRESETS: EditorSignerRole[] = [
  { role: "customer", label: "Customer", order: 0, defaultChannel: "email" },
  { role: "co_signer", label: "Co-signer", order: 1, defaultChannel: "email" },
  { role: "dealer_rep", label: "Dealer rep", order: 2, defaultChannel: "embedded" },
];

export const FIELD_KIND_DEFAULTS: Record<
  TemplateFieldKind,
  { width: number; height: number; label: string }
> = {
  signature: { width: 180, height: 40, label: "Signature" },
  initials: { width: 60, height: 28, label: "Initials" },
  date: { width: 100, height: 24, label: "Date" },
  text: { width: 160, height: 22, label: "Text" },
  checkbox: { width: 18, height: 18, label: "Checkbox" },
};

export function newEditorFieldId(kind: TemplateFieldKind): string {
  return `${kind}_${crypto.randomUUID().slice(0, 8)}`;
}

export function serializeEditorFieldMap(fieldMap: EditorFieldMap): EditorFieldMap {
  return {
    fields: fieldMap.fields.map((f) => ({
      id: f.id,
      kind: f.kind,
      page: f.page,
      x: Math.round(f.x * 10) / 10,
      y: Math.round(f.y * 10) / 10,
      width: Math.round(f.width * 10) / 10,
      height: Math.round(f.height * 10) / 10,
      ...(f.signerRole ? { signerRole: f.signerRole } : {}),
      ...(f.dataPath ? { dataPath: f.dataPath } : {}),
      required: f.required !== false,
    })),
  };
}

export function clampFieldToPage(
  field: EditorField,
  pageWidth: number,
  pageHeight: number
): EditorField {
  const width = Math.min(Math.max(field.width, 12), pageWidth);
  const height = Math.min(Math.max(field.height, 12), pageHeight);
  const x = Math.min(Math.max(field.x, 0), Math.max(0, pageWidth - width));
  const y = Math.min(Math.max(field.y, 0), Math.max(0, pageHeight - height));
  return { ...field, x, y, width, height };
}

export function resolvePreviewValue(
  dataPath: string | undefined,
  fixture: Record<string, unknown>
): string | undefined {
  if (!dataPath?.trim()) return undefined;
  const parts = dataPath.split(".").map((p) => p.trim()).filter(Boolean);
  let cur: unknown = fixture;
  for (const part of parts) {
    if (!cur || typeof cur !== "object" || Array.isArray(cur)) return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  if (cur === null || cur === undefined) return undefined;
  return String(cur);
}

export function fieldKindUsesSignerRole(kind: TemplateFieldKind): boolean {
  return kind === "signature" || kind === "initials" || kind === "date";
}

export function fieldKindUsesDataPath(kind: TemplateFieldKind): boolean {
  return kind === "text";
}
